import { useMutation } from "@apollo/client";
import {
  Button,
  Divider,
  FileInput,
  Group,
  Image,
  Loader,
  MultiSelect,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconUpload } from "@tabler/icons-react";
import { useState } from "react";
import { User, UserType } from "../../../../gql/__generated__/graphql";
import { GENERATE_UPLOAD_URL } from "../../../apis/lambda/GraphQL/Upload/mutations";

interface ProfileEditFormProps {
  profile: User;
  onSave: (updatedProfile: User) => void;
  onCancel: () => void;
  role: UserType;
}

const ProfileEditForm = ({
  profile,
  onSave,
  onCancel,
  role,
}: ProfileEditFormProps) => {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [useDirectUrl, setUseDirectUrl] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    profile.imageUrl || undefined
  );

  const [generateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);

  const form = useForm({
    initialValues: {
      name: profile.name || "",
      email: profile.email || "",
      description: profile.description || "",
      categories: profile.categories || [],
      imageUrl: profile.imageUrl || "",
      socialMedia: profile.socialMedia || null,
    },
    validate: {
      name: (name: string) => (name ? null : "Name is required"),
      email: (email: string) =>
        !email || /^\S+@\S+$/.test(email) ? null : "Invalid email",
      description: (description: string) =>
        description ? null : "Description is required",
      categories: (categories: string[]) =>
        !categories || categories.length === 0
          ? "Select at least one category"
          : categories.length > 3
          ? "Maximum 3 categories allowed"
          : null,
    },
  });

  const handleSocialMediaChange = (platform: string, value: number | "") => {
    form.setFieldValue(`socialMedia.${platform}`, value === "" ? null : value);
  };

  const handleFileChange = (file: File | null) => {
    setLogoFile(file);
    if (file) {
      // Create local preview
      const localPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(localPreviewUrl);
    }
  };

  const handleDirectUrlChange = (url: string) => {
    form.setFieldValue("imageUrl", url);
    setPreviewUrl(url);
  };

  const uploadImageToS3 = async (file: File): Promise<string> => {
    try {
      // Step 1: Get pre-signed upload URL from backend
      const { data } = await generateUploadUrl({
        variables: {
          input: {
            fileName: file.name,
            fileType: file.type,
            folder: "profile-images",
          },
        },
      });

      if (!data?.generateUploadUrl) {
        throw new Error("Failed to generate upload URL");
      }

      const { uploadUrl, fileUrl } = data.generateUploadUrl;

      // Step 2: Upload file directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to S3");
      }

      return fileUrl;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    setIsUploading(true);
    try {
      let finalImageUrl = values.imageUrl;

      // If user uploaded a file, upload it to S3 first
      if (logoFile && !useDirectUrl) {
        try {
          finalImageUrl = await uploadImageToS3(logoFile);
          notifications.show({
            title: "Success",
            message: "Image uploaded successfully",
            color: "green",
          });
        } catch (error) {
          notifications.show({
            title: "Upload Error",
            message: "Failed to upload image. Please try again.",
            color: "red",
          });
          setIsUploading(false);
          return;
        }
      }

      // Merge the form values with the original profile
      const updatedProfile: User = {
        ...profile,
        ...values,
        imageUrl: finalImageUrl,
      };

      onSave(updatedProfile);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to save profile",
        color: "red",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const categoryOptions = [
    { value: "fashion", label: "Fashion" },
    { value: "beauty", label: "Beauty" },
    { value: "fitness", label: "Fitness" },
    { value: "food", label: "Food & Beverage" },
    { value: "tech", label: "Technology" },
    { value: "lifestyle", label: "Lifestyle" },
    { value: "travel", label: "Travel" },
    { value: "gaming", label: "Gaming" },
    { value: "health", label: "Health & Wellness" },
    { value: "home", label: "Home & Decor" },
    { value: "sustainability", label: "Sustainability" },
  ];

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="xl">
          <Title order={2}>Edit Profile</Title>

          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={500}>Profile Picture</Text>
              <Switch
                label="Use direct URL"
                checked={useDirectUrl}
                onChange={(event) =>
                  setUseDirectUrl(event.currentTarget.checked)
                }
              />
            </Group>

            {useDirectUrl ? (
              <TextInput
                label="Image URL"
                placeholder="https://example.com/image.jpg"
                value={form.values.imageUrl}
                onChange={(event) =>
                  handleDirectUrlChange(event.currentTarget.value)
                }
              />
            ) : (
              <FileInput
                label="Upload Image"
                placeholder="Choose an image file"
                accept="image/*"
                leftSection={<IconUpload size={16} />}
                value={logoFile}
                onChange={handleFileChange}
              />
            )}

            {previewUrl && (
              <Group>
                <Text size="sm" c="dimmed">
                  Preview:
                </Text>
                <Image
                  src={previewUrl}
                  alt="Profile preview"
                  w={100}
                  h={100}
                  radius="md"
                  fit="cover"
                />
              </Group>
            )}
          </Stack>

          <TextInput
            label="Name"
            placeholder="Your name or brand name"
            {...form.getInputProps("name")}
            required
          />

          <MultiSelect
            label="Categories (up to 3)"
            placeholder="Select categories"
            data={categoryOptions}
            maxValues={3}
            {...form.getInputProps("categories")}
            required
          />

          <Textarea
            label="Description"
            placeholder="Tell us about yourself or your brand"
            minRows={4}
            {...form.getInputProps("description")}
            required
          />

          <Divider label="Contact Information" labelPosition="center" />

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <TextInput
              label="Email"
              placeholder="contact@example.com"
              {...form.getInputProps("email")}
            />
          </SimpleGrid>

          {role === "CREATOR" && (
            <>
              <Divider label="Social Media Followers" labelPosition="center" />

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                <NumberInput
                  label="Instagram Followers"
                  placeholder="e.g. 10000"
                  value={form.values.socialMedia?.instagram || ""}
                  onChange={(value) =>
                    handleSocialMediaChange("instagram", Number(value))
                  }
                />

                <NumberInput
                  label="YouTube Subscribers"
                  placeholder="e.g. 25000"
                  value={form.values.socialMedia?.youtube || ""}
                  onChange={(value) =>
                    handleSocialMediaChange("youtube", Number(value))
                  }
                />

                <NumberInput
                  label="Twitter Followers"
                  placeholder="e.g. 5000"
                  value={form.values.socialMedia?.twitter || ""}
                  onChange={(value) =>
                    handleSocialMediaChange("twitter", Number(value))
                  }
                />
              </SimpleGrid>
            </>
          )}

          <Group justify="flex-end" mt="xl">
            <Button variant="outline" onClick={onCancel} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="secondary"
              loading={isUploading}
              leftSection={isUploading ? <Loader size="xs" /> : undefined}
            >
              {isUploading ? "Saving..." : "Save Changes"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
};

export default ProfileEditForm;
